import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navActivityLabel } from "@fomofi/shared";
import { REDESIGN_PHONE_TABS as PHONE_TABS, redesignLit as lit } from "@/lib/redesignPhoneTabs";
import { cn } from "@/lib/utils";
import { useProfile } from "@/hooks/use-profile";

/**
 * Phone-width bottom tabs (Anthony's Transactions redesign, Phase 3; decision note in
 * report -102, approved by Paul 2026-10-08). Below 768px only: tablets keep the header
 * and drawer, desktop keeps the sidebar, both unchanged.
 *
 * - Five tabs, the redesign's five (2026-10-13): Home, Plan, Activity, Upcoming,
 *   Insights, the same items as the sidebar. More is gone: what it listed is a
 *   section tab under one of the five, or in the header's account menu.
 * - Sheets, side panels and dialogs are z-50 overlays; the bar is z-40, so a panel's
 *   Save is never under it.
 * - The bar hides while a text field has focus, so with the keyboard open it can never
 *   cover the field being typed in.
 * - It clears the iPhone home indicator with env(safe-area-inset-bottom) (viewport-fit
 *   is cover), and `--tabbar-space` moves the feedback button and Back to top above it.
 */
/** True while a text field has focus: the on-screen keyboard is up. */
function useTypingFocus() {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.isContentEditable ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLInputElement &&
          !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(
            el.type,
          )));
    const onIn = (e: FocusEvent) => setTyping(isTextField(e.target));
    const onOut = () => setTyping(isTextField(document.activeElement));
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return typing;
}

const itemClass = (active: boolean) =>
  cn(
    "flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-[11px] font-medium leading-tight transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active ? "text-primary" : "text-muted-foreground hover:text-foreground",
  );

/**
 * A label, shown for every tab when five fit across, and for the ACTIVE tab only when
 * they don't (a large text size, or 200% zoom: "Transactions" can't fit a fifth of
 * 390px at 2x; rule 15, decision in report -98 Q6). Every tab keeps its accessible name
 * either way. The breakpoint is in rem, so it follows the user's text size.
 */
function TabLabel({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={cn("max-w-full truncate", !active && "hidden @[22rem]:block")}>{label}</span>
  );
}

export default function PhoneTabBarRedesign() {
  const { pathname } = useLocation();
  const typing = useTypingFocus();
  // RD-02/RD-04: the Activity tab's dot, this pay period's purchases to sort (Q6).
  const toSortCount = useProfile().data?.features?.activity_to_sort_count ?? 0;

  return (
    <>
      <nav
        aria-label="Main"
        data-phone-tabbar=""
        className={cn(
          "@container fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur-xl md:hidden",
          "pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]",
          typing && "hidden",
        )}
      >
        {/* Horizontal padding is trimmed so "Transactions" fits its fifth (Paul's iPhone 17 Pro
            check, 2026-10-09): at 11px medium Plus Jakarta Sans it measures 66.34px, and the
            old px-2 / gap-1 / item px-1 left 66.0px at 402pt and 63.6px at 390pt, so it read
            "Transactio…". px-1 / no gap / item px-0.5 leaves 74.8px and 72.4px. */}
        <ul className="flex h-[var(--tabbar-h)] items-stretch px-1" role="list">
          {PHONE_TABS.map((tab) => {
            const active = lit(pathname, tab.lights);
            const count = tab.to === "/transactions" ? toSortCount : 0;
            return (
              <li key={tab.to} className="flex min-w-0 flex-1" role="listitem">
                {/* Link, not NavLink: NavLink sets aria-current from its own `to` alone,
                    and a tab here stands for several pages (Plan lights on /budgets). */}
                <Link
                  to={tab.to}
                  aria-label={count > 0 ? navActivityLabel(count) : tab.label}
                  aria-current={active ? "page" : undefined}
                  className={itemClass(active)}
                >
                  <span className="relative shrink-0">
                    <tab.icon className="h-5 w-5" aria-hidden="true" />
                    {count > 0 && (
                      <span
                        aria-hidden="true"
                        data-activity-dot=""
                        className="absolute -right-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-warning ring-2 ring-background"
                      />
                    )}
                  </span>
                  <TabLabel label={tab.label} active={active} />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
